import compose from 'lodash/fp/flow';
import get from 'lodash/fp/get';
import map from 'lodash/fp/map';
import merge from 'lodash/fp/merge';
import range from 'lodash/fp/range';
import uniq from 'lodash/fp/uniq';
import { stringify } from 'query-string';
import { compile } from 'path-to-regexp';
import { createSelector } from 'reselect';

import createGetContentItem from 'selectors/createGetContentItem';
import getActivePagination from 'selectors/getActivePagination';
import getMatch from 'selectors/getMatch';
import getPathMap from 'selectors/getPathMap';
import getTerm from 'selectors/getTerm';
import createGetTranslation from 'selectors/i18n';
import getContentArchive from './getContentArchiveBase';

export const getContentArchivePageMeta = createSelector(
  [
    getContentArchive,
    getMatch,
    createGetTranslation,
  ],
  (meta, { query }, getTranslation) => {
    if (!meta) {
      return {
        pages: {},
        controls: {},
      };
    }

    const { order, filters: { term } } = meta;

    const toControl = (controlMap, selectedPath) => ({
      selected: get(selectedPath, query),
      options: Object
        .keys(controlMap)
        .map((value) => ({
          value,
          label: getTranslation(value, controlMap[value]),
        })),
    });

    const termFilters = Object
      .keys(term)
      .map((taxonomy) => ({ [taxonomy]: toControl(term[taxonomy], taxonomy) }))
      .reduce(merge, {});

    return {
      pages: meta.pages,
      controls: {
        order: toControl(order, 'order'),
        ...termFilters,
      },
    };
  },
);

export const getPaginationData = createSelector(
  [
    getActivePagination,
    getMatch,
    getPathMap,
  ],
  ({ totalPages, currentPage }, { params, query, pathType }, pathMap) => ({
    hasMore: totalPages ? 1 < totalPages && currentPage < totalPages : false,
    currentPage,
    totalPages,
    paginationLinks: range(1, totalPages + 1)
      .map((val, pageIndex) => {
        const toPath = compile(pathMap[pathType]);
        const basePath = toPath({
          ...params,
          page: pageIndex + 1,
        });
        const queryString = stringify(query) ? `?${stringify(query)}` : '';

        return `${basePath}${queryString}`;
      }, []),
  }),
);

export const getContentArchiveItems = createSelector(
  [
    getActivePagination,
    getContentArchivePageMeta,
    getMatch,
    (state) => state,
  ],
  ({ currentPage }, { pages }, { params: { page: routePage } }, state) => {
    // Prevent a malicious payload from crashing the server with a huge page number
    const maxPageCount = 1000;
    if (currentPage > maxPageCount) {
      console.warn( // eslint-disable-line no-console
        `WARNING: page ${currentPage} exceeds max of ${maxPageCount}; short-circuiting and returning no results`,
      );
      return [];
    }

    const mapItem = ({ type, id }) =>
      createGetContentItem(type)(state, id);

    // If we're viewing only the content for a specific page, determined by route :page param
    if (routePage && pages[routePage] && Array.isArray(pages[routePage])) {
      return pages[routePage]
        .map(mapItem)
        .reduce((acc, ids) => acc.concat(ids), []);
    }

    // If we're using the loadMore fuctionality
    return range(1, currentPage + 1)
      .map((page) => {
        const refs = pages[page] || [];
        return refs.map(mapItem);
      })
      .reduce((acc, ids) => acc.concat(ids), []);
  },
);

/**
 * @typedef {Object} ContentArchive
 * @property {object[]} items
 * @property {function} component
 * @property {object} pagination
 *
 * Select and compute a content archive shape object.
 * @param {object} state Redux state
 * @return {ContentArchive}
 */
export default createSelector(
  [
    getContentArchivePageMeta,
    getContentArchiveItems,
    getPaginationData,
    getTerm,
  ],
  (pageMeta, items, pagination, term) => {
    const contentTypes = compose(map('metadata.type'), uniq)(items);
    const contentType = 1 === contentTypes.length ? contentTypes[0] : null;
    const typeLabel = contentType ? get('metadata.postTypeObject.label', items[0]) : null;

    return {
      items,
      pagination,
      pageMeta,
      contentType,
      typeLabel,
      term,
    };
  },
);
